/**
 * Web Push and energy data-request client. Wraps the `/notifications` API routes through the shared
 * credentialed `request()` helper, and holds the browser-side push flow: request permission, find or
 * register the service worker, subscribe with the server's VAPID key, and upload the subscription.
 * When the server says the browser's endpoint belongs to another account (409), the old
 * subscription is dropped and a fresh one is created, which gives this account its own endpoint.
 *
 * @module apps/web/src/lib/notificationsApi
 */

import { request } from '@/lib/api';

/** One data request the server sent (or prepared for the in-app prompt) in the last 14 days. */
export interface DataRequestNotification {
  /** Notification row id, reported back when opened. */
  id: string;
  /** Recipient's local date (`YYYY-MM-DD`); at most one request exists per date. */
  localDate: string;
  /** How it was delivered: `push`, `email`, or `in_app` when neither was available. */
  channel: 'push' | 'email' | 'in_app';
  /** Why it was sent: `flux_agent_request`, `forecast_uncertainty` or `stale_check_in`. */
  reason: string;
  /** Flux Agent insight that supplied the question, when one did and still exists. */
  insightId: string | null;
  /** UTC send time as stored by D1. */
  sentAt: string;
  /** UTC time it was first opened, or null while unopened. */
  openedAt: string | null;
  /** The question to answer. */
  question: string;
  /** App-relative deep link that opens Flux Agent voice mode on the question. */
  url: string;
}

/** Push delivery counts returned by the test route. */
export interface PushTestResult {
  /** Subscriptions the push service accepted. */
  delivered: number;
  /** Subscriptions deleted because the push service reported them gone. */
  removed: number;
  /** Subscriptions whose delivery failed. */
  failed: number;
}

/** Browser-side push state shown in Settings. */
export type PushStatus =
  | 'unsupported'
  | 'needs-install'
  | 'not-configured'
  | 'denied'
  | 'off'
  | 'on';

/** Notification API routes; every call is scoped to the signed-in user by the server. */
export const notificationsApi = {
  /** Fetch the VAPID public key; fails with `PUSH_NOT_CONFIGURED` (503) when push is disabled. */
  publicKey: () => request<{ publicKey: string }>('/notifications/push/public-key'),
  /** Store this browser's subscription for the caller. */
  subscribe: (subscription: PushSubscriptionJSON) =>
    request<{ subscribed: boolean }>('/notifications/push/subscriptions', {
      method: 'POST',
      body: JSON.stringify(subscription),
    }),
  /** Remove the caller's subscription with this endpoint. */
  unsubscribe: (endpoint: string) =>
    request<{ removed: number }>('/notifications/push/subscriptions', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
    }),
  /** Send a test notification to the caller's subscriptions (once per minute). */
  sendTest: () => request<PushTestResult>('/notifications/push/test', { method: 'POST', body: '{}' }),
  /** The caller's recent data requests, newest first. */
  dataRequests: () => request<{ requests: DataRequestNotification[] }>('/notifications/data-requests'),
  /** Record that the caller opened a data request; repeated calls keep the first time. */
  markOpened: (id: string) =>
    request<{ opened: boolean }>(`/notifications/data-requests/${encodeURIComponent(id)}/opened`, {
      method: 'POST',
      body: '{}',
    }),
};

/** True on iPhone/iPad Safari, including iPadOS reporting as a Mac with touch. */
export function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

/** True when running as an installed app (Home Screen / standalone display). */
export function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** True when this browser exposes service workers, the Push API and notifications. */
export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** Decode a base64url VAPID key into the bytes `applicationServerKey` expects. */
export function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(value.length / 4) * 4, '=');
  const binary = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

/** The active service-worker registration, registering `/sw.js` when none exists (development builds). */
async function registration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;
  await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
  return navigator.serviceWorker.ready;
}

/** Current push status for Settings, without prompting. iOS outside the Home Screen app cannot subscribe. */
export async function pushStatus(): Promise<PushStatus> {
  if (!pushSupported()) return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const existing = await navigator.serviceWorker.getRegistration();
  const subscription = await existing?.pushManager.getSubscription();
  return subscription ? 'on' : 'off';
}

/** Result of turning push on: the new status plus a user-facing message on failure. */
export interface EnablePushResult {
  /** Status after the attempt. */
  status: PushStatus;
  /** Explanation when the attempt did not end with push on. */
  message?: string;
}

/** Ask for permission, subscribe with the server key and upload the subscription. */
export async function enablePush(): Promise<EnablePushResult> {
  if (!pushSupported()) {
    return isIos() && !isStandalone()
      ? { status: 'needs-install', message: 'Add Fluxability to your Home Screen to get notifications.' }
      : { status: 'unsupported', message: 'This browser does not support push notifications.' };
  }
  const key = await notificationsApi.publicKey();
  if (!key.success || !key.data?.publicKey) {
    return key.error?.code === 'PUSH_NOT_CONFIGURED'
      ? { status: 'not-configured', message: 'Push notifications are not available on this server yet.' }
      : { status: 'off', message: key.error?.message || 'Could not reach Fluxability.' };
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return {
      status: permission === 'denied' ? 'denied' : 'off',
      message: 'Allow notifications for Fluxability in your browser settings.',
    };
  }
  const worker = await registration();
  const options = { userVisibleOnly: true, applicationServerKey: base64UrlToBytes(key.data.publicKey) };
  let subscription = await worker.pushManager.getSubscription();
  if (!subscription) subscription = await worker.pushManager.subscribe(options);
  let saved = await notificationsApi.subscribe(subscription.toJSON());
  if (!saved.success && saved.status === 409) {
    // The endpoint is registered to another account: get a fresh endpoint for this one.
    await subscription.unsubscribe().catch(() => false);
    subscription = await worker.pushManager.subscribe(options);
    saved = await notificationsApi.subscribe(subscription.toJSON());
  }
  if (!saved.success) return { status: 'off', message: saved.error?.message || 'Could not save this device.' };
  return { status: 'on' };
}

/** Remove this browser's subscription from the server and the push service. */
export async function disablePush(): Promise<void> {
  const existing = await navigator.serviceWorker.getRegistration();
  const subscription = await existing?.pushManager.getSubscription();
  if (!subscription) return;
  await notificationsApi.unsubscribe(subscription.endpoint);
  await subscription.unsubscribe().catch(() => false);
}

/** Show a local notification through the service worker when one is active, else the page Notification API. */
export async function showLocalNotification(title: string, options: NotificationOptions): Promise<void> {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  const worker = 'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistration() : undefined;
  if (worker) {
    await worker.showNotification(title, options);
    return;
  }
  new Notification(title, options);
}
